/**
 * Global theme controller.
 *
 * A single boolean per surface (admin vs public) determines whether the
 * ``dark`` CSS class is applied to <html>. Values are fetched from
 * ``/api/settings/platform/public`` so anonymous visitors see the correct
 * theme without an auth token, and are refreshed on every route change.
 *
 * Any component can call ``useTheme()`` to read the active flag or the
 * ``refresh()`` helper to re-pull after an admin toggle.
 */

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";


const ThemeContext = createContext(null);


export const ThemeProvider = ({ children }) => {
    const [adminDark, setAdminDark] = useState(true);
    const [publicDark, setPublicDark] = useState(true);
    const location = useLocation();

    const isAdminSurface = location.pathname.startsWith("/admin");

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/settings/platform/public`, { timeout: 10000 });
            const t = r.data?.theme || {};
            if (typeof t.admin_dark_mode === "boolean") setAdminDark(t.admin_dark_mode);
            if (typeof t.public_dark_mode === "boolean") setPublicDark(t.public_dark_mode);
        } catch (_e) {
            // network hiccup — keep last known values
        }
    }, []);

    // Fetch on mount + whenever the surface flips (admin ↔ public)
    useEffect(() => { refresh(); }, [refresh]);

    // Apply the dark class to <html> so downstream Tailwind ``dark:`` variants
    // + our custom light overrides in index.css kick in immediately.
    useEffect(() => {
        const dark = isAdminSurface ? adminDark : publicDark;
        const root = document.documentElement;
        if (dark) {
            root.classList.add("dark");
            root.classList.remove("light");
            root.dataset.theme = "dark";
        } else {
            root.classList.remove("dark");
            root.classList.add("light");
            root.dataset.theme = "light";
        }
    }, [isAdminSurface, adminDark, publicDark]);

    return (
        <ThemeContext.Provider value={{ adminDark, publicDark, refresh, isAdminSurface }}>
            {children}
        </ThemeContext.Provider>
    );
};


export const useTheme = () => {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
};
